import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api';
import ChevronDownIcon from '../../assets/icons/chevron-down.svg?react';
import SearchIcon from '../../assets/icons/search.svg?react';
import './Selector.css';

/**
 * SelectorItem 데이터 구조
 * @typedef {Object} SelectorItem
 * @property {string} id - 고유 식별자
 * @property {string} label - 표시될 텍스트
 * @property {*} data - 추가 데이터 (선택 시 반환됨)
 */

/**
 * Selector 컴포넌트
 * @param {Object} props
 * @param {'local' | 'api'} props.mode - 'local': 로컬 데이터 필터링, 'api': API 호출
 * @param {SelectorItem[]} props.options - 로컬 모드일 때 사용할 옵션 목록
 * @param {string} props.apiEndpoint - API 모드일 때 사용할 엔드포인트
 * @param {Function} props.apiQueryFn - API 모드일 때 사용할 커스텀 쿼리 함수 (선택)
 * @param {Object} props.apiQueryKeys - API 모드일 때 사용할 쿼리 키 (선택)
 * @param {Function} props.dataTransform - API 응답을 SelectorItem[] 형태로 변환하는 함수
 * @param {Function} props.buildApiParams - 검색어를 API 파라미터로 변환하는 함수
 * @param {SelectorItem} props.value - 선택된 값
 * @param {Function} props.onChange - 선택 변경 시 호출 (selectedItem을 인자로 받음)
 * @param {string} props.placeholder - 플레이스홀더 텍스트
 * @param {boolean} props.enableSearch - 검색 기능 활성화 여부
 * @param {number} props.searchDebounceMs - 검색 디바운스 시간 (ms)
 * @param {string} props.searchPlaceholder - 검색 입력 플레이스홀더
 * @param {string} props.emptyMessage - 데이터가 없을 때 표시할 메시지
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {string} props.className - 추가 CSS 클래스
 * @param {boolean} props.creatable - 새 항목 생성 가능 여부 (검색어가 옵션에 없을 때)
 * @param {string} props.createLabel - 새 항목 생성 버튼 텍스트 (기본: "새로 만들기: {검색어}")
 * @param {'default' | 'lg'} props.size - 크기 설정 (기본: 'default' (40px), 'lg' (54px))
 */
const Selector = ({
  mode = 'local',
  options = [],
  apiEndpoint = '',
  apiQueryFn = null,
  apiQueryKeys = null,
  dataTransform = (data) => data,
  buildApiParams = (searchQuery) => ({ search: searchQuery }),
  value = null,
  onChange = () => {},
  placeholder = '선택하세요',
  enableSearch = true,
  searchDebounceMs = 300,
  searchPlaceholder = '검색...',
  emptyMessage = '데이터가 없습니다',
  disabled = false,
  className = '',
  creatable = false,
  createLabel = null, // null이면 기본 텍스트 사용
  size = 'default', // 'default' | 'lg'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Debounce 검색어
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, searchDebounceMs]);

  // API 모드: React Query 사용
  const { data: apiData, isLoading: apiLoading, error: apiError } = useQuery({
    enabled: mode === 'api' && isOpen && enableSearch,
    queryKey: apiQueryKeys ? apiQueryKeys(debouncedSearchQuery) : ['selector', apiEndpoint, debouncedSearchQuery],
    queryFn: async () => {
      if (apiQueryFn) {
        return await apiQueryFn(debouncedSearchQuery);
      }
      const params = buildApiParams(debouncedSearchQuery);
      const response = await api.get(apiEndpoint, { params });
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });

  // API 응답을 SelectorItem 배열로 변환
  const apiOptions = useMemo(() => {
    if (mode !== 'api' || !apiData) return [];
    return dataTransform(apiData);
  }, [mode, apiData, dataTransform]);

  // 로컬 모드: 검색어로 필터링
  const filteredLocalOptions = useMemo(() => {
    if (mode !== 'local' || !enableSearch || !debouncedSearchQuery.trim()) {
      return options;
    }
    const query = debouncedSearchQuery.toLowerCase();
    return options.filter((item) =>
      item.label?.toLowerCase().includes(query) ||
      item.id?.toLowerCase().includes(query)
    );
  }, [mode, options, debouncedSearchQuery, enableSearch]);

  // 현재 사용할 옵션 목록
  const currentOptions = mode === 'local' ? filteredLocalOptions : apiOptions;

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // 드롭다운이 열릴 때 검색 입력 필드에 포커스
      if (enableSearch && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, enableSearch]);

  // 선택 처리
  const handleSelect = (item) => {
    onChange(item);
    setIsOpen(false);
    setSearchQuery('');
  };

  // 새 항목 생성 처리
  const handleCreateNew = () => {
    if (!searchQuery.trim()) return;
    
    const newItem = {
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      label: searchQuery.trim(),
      data: { isNew: true, label: searchQuery.trim() },
    };
    
    handleSelect(newItem);
  };

  // 검색어가 현재 옵션에 있는지 확인
  const hasMatchingOption = useMemo(() => {
    if (!searchQuery.trim()) return true; // 검색어가 없으면 true (모든 옵션 표시)
    
    const query = searchQuery.trim().toLowerCase();
    return currentOptions.some(
      (item) => item.label?.toLowerCase() === query || item.id?.toLowerCase() === query
    );
  }, [currentOptions, searchQuery]);

  // creatable 모드에서 새 항목 생성 가능 여부
  const canCreateNew = creatable && searchQuery.trim() && !hasMatchingOption;

  // 드롭다운 토글
  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      // 드롭다운을 열 때, 선택된 값이 있으면 검색어를 초기화
      // (검색어가 있으면 필터링된 결과만 보이므로)
      setSearchQuery('');
    }
  };

  // 검색어 변경
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // 선택된 항목의 라벨
  const selectedLabel = value?.label || placeholder;

  return (
    <div className={`selector-container ${className} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      {/* 선택 버튼 */}
      <div
        className={`selector-trigger selector-size-${size} ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
      >
        <span className={`selector-label ${!value ? 'placeholder' : ''}`}>
          {selectedLabel}
        </span>
        <ChevronDownIcon className={`selector-chevron ${isOpen ? 'up' : ''}`} />
      </div>

      {/* 드롭다운 패널 */}
      {isOpen && !disabled && (
        <div className="selector-dropdown">
          {/* 검색 입력 (활성화된 경우) */}
          {enableSearch && (
            <div className="selector-search">
              <SearchIcon className="selector-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="selector-search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          )}

          {/* 옵션 목록 */}
          <div className="selector-options">
            {mode === 'api' && apiLoading ? (
              <div className="selector-loading">로딩 중...</div>
            ) : mode === 'api' && apiError ? (
              <div className="selector-error">데이터를 불러오는데 실패했습니다.</div>
            ) : currentOptions.length === 0 && !canCreateNew ? (
              <div className="selector-empty">{emptyMessage}</div>
            ) : (
              <>
                {currentOptions.map((item) => (
                  <div
                    key={item.id}
                    className={`selector-option ${value?.id === item.id ? 'selected' : ''}`}
                    onClick={() => handleSelect(item)}
                  >
                    {item.label}
                  </div>
                ))}
                {/* 새 항목 생성 옵션 */}
                {canCreateNew && (
                  <div
                    className="selector-option selector-option-create"
                    onClick={handleCreateNew}
                  >
                    {createLabel
                      ? createLabel.replace('{검색어}', searchQuery.trim())
                      : `새로 만들기: "${searchQuery.trim()}"`}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Selector;

